import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { ThemeContext } from 'styled-components';
import type { DataType, NodeInstanceDataPort, NodeInstanceState, UserDefinedType } from '@glueapp/graph-types';
import type { UnpackSelections } from '@glueapp/graph-nodes';
import type { ThemeType } from '@/theme/glueTheme';
import { TypeRenderer } from './TypeRenderer';
import { PortQuickActionFooter } from './PortQuickActionFooter';
import { useCurrentNodeInstance } from '@/GraphEditor/drawingLayer/contexts/CurrentNodeInstanceContext';
import { useCurrentNode } from '@/GraphEditor/drawingLayer/contexts/CurrentNodeContext';
import { useMergeInGraph } from '@/GraphEditor/drawingLayer/contexts/MergeInGraphContext';
import { useGraphDispatch } from '@/GraphEditor/drawingLayer/contexts/GraphDispatchContext';
import { setNodeState, setPortUserDefinedType } from '@/GraphEditor/actions';
import { buildUnpackSelectionForPath, createUnpackFromOutPort } from './createQuickActionNode';
import { ShapeTypeSelectorEditor } from '@/NodeInstanceEditor/ShapeValueEditor/ShapeValueEditor';
import { canHaveUserDefinedType, getEditorRestrictionForPort, getInitialUserDefinedType } from './portUserTypeEligibility';
import { NodeEventInsulator } from '@/GraphEditor/drawingLayer/graphComponents/NodeInterfaceEventInsulator';
import { Settings, X } from 'lucide-react';

interface TypeDisplayPopoverProps {
    port: NodeInstanceDataPort;
    side: 'in' | 'out';
    visible: boolean;
    popoverHoverProps: {
        onMouseEnter: () => void;
        onMouseLeave: () => void;
    };
    mode?: 'display' | 'edit';
    onRequestClose?: () => void;
    onRequestEdit?: () => void;
}

export const TypeDisplayPopover: React.FC<TypeDisplayPopoverProps> = ({
    port,
    side,
    visible,
    popoverHoverProps,
    mode = 'display',
    onRequestClose,
    onRequestEdit,
}) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const theme = useContext<ThemeType>(ThemeContext);
    const nodeInstance = useCurrentNodeInstance();
    const node = useCurrentNode();
    const mergeInGraph = useMergeInGraph();
    const graphDispatch = useGraphDispatch();

    const stopPropagation = useCallback((event: { stopPropagation: () => void }) => {
        event.stopPropagation();
    }, []);

    // Tracks the Unpack node created in the current popover session so that
    // successive property clicks accumulate into the same node. Reset when the
    // popover hides (hover-away ends the session).
    const sessionRef = useRef<{ unpackId: number; selections: UnpackSelections } | null>(null);
    useEffect(() => {
        if (!visible) {
            sessionRef.current = null;
        }
    }, [visible]);

    const existingUserDefinedType: UserDefinedType | undefined = nodeInstance.portUserDefinedTypes?.[port.name];
    const existingCustomType =
        existingUserDefinedType && existingUserDefinedType.kind === 'customType' ? existingUserDefinedType.type : undefined;

    const declaredPortType = useMemo(() => {
        const declaredPorts = side === 'in' ? node.inPorts : node.outPorts;
        return declaredPorts.find(p => p.name === port.name)?.type ?? port.type;
    }, [node.inPorts, node.outPorts, port.name, port.type, side]);
    const editorRestriction = useMemo(() => getEditorRestrictionForPort(declaredPortType), [declaredPortType]);
    const editorInitialValue = useMemo(
        () => existingCustomType ?? getInitialUserDefinedType(declaredPortType),
        [existingCustomType, declaredPortType],
    );

    const handleCustomTypeChange = useCallback(
        (newType: DataType) => {
            if (!graphDispatch) return;
            graphDispatch(setPortUserDefinedType(nodeInstance.id, port.name, { kind: 'customType', type: newType }));
        },
        [graphDispatch, nodeInstance.id, port.name],
    );

    const onPropertyClick =
        side === 'out' && mergeInGraph && mode === 'display'
            ? (dotPath: string) => {
                  const session = sessionRef.current;
                  if (session && graphDispatch) {
                      if (dotPath in session.selections) return;
                      const selection = buildUnpackSelectionForPath(port.type, dotPath);
                      if (!selection) return;
                      const nextSelections: UnpackSelections = { ...session.selections, [dotPath]: selection };
                      sessionRef.current = { unpackId: session.unpackId, selections: nextSelections };
                      graphDispatch(
                          setNodeState(session.unpackId, { selections: nextSelections, expanded: true } as unknown as NodeInstanceState),
                      );
                      return;
                  }
                  const assignedId = createUnpackFromOutPort({
                      mergeInGraph,
                      sourceNode: nodeInstance,
                      sourcePortName: port.name,
                      sourcePortType: port.type,
                      selectedPath: dotPath,
                  });
                  const selection = buildUnpackSelectionForPath(port.type, dotPath);
                  if (assignedId !== undefined && selection) {
                      sessionRef.current = { unpackId: assignedId, selections: { [dotPath]: selection } };
                  }
              }
            : undefined;

    useEffect(() => {
        const element = cardRef.current;
        if (!element) return;
        element.addEventListener('wheel', stopPropagation, { passive: false });
        return () => {
            element.removeEventListener('wheel', stopPropagation);
        };
    }, [stopPropagation, visible]);

    const isEditing = mode === 'edit';
    const canEditType = canHaveUserDefinedType(declaredPortType);

    useEffect(() => {
        if (!isEditing || !onRequestClose) return;
        const handleDocumentPointerDown = (event: MouseEvent) => {
            const element = cardRef.current;
            if (!element) return;
            if (!(event.target instanceof Element)) return;
            if (element.contains(event.target)) return;
            // Ignore clicks on the gear button — it owns the open/close toggle itself.
            if (event.target.closest('[data-type-edit-trigger="true"]')) return;
            onRequestClose();
        };
        document.addEventListener('mousedown', handleDocumentPointerDown);
        return () => {
            document.removeEventListener('mousedown', handleDocumentPointerDown);
        };
    }, [isEditing, onRequestClose]);

    if (!visible) {
        return null;
    }

    return (
        <div
            ref={cardRef}
            className={`absolute top-0 -translate-y-1/2 pointer-events-auto rounded-lg overflow-y-auto w-fit text-xs z-10 ${
                side === 'in' ? 'right-4' : 'left-4'
            }`}
            style={{
                backgroundColor: theme.elevatedBackgroundColor,
                color: theme.primaryContrastColor,
                border: `2px solid ${theme.separatorColor}`,
                padding: '8px 10px',
                maxHeight: 560,
                minWidth: isEditing ? 240 : undefined,
                animation: 'fadeIn 75ms ease forwards',
            }}
            {...popoverHoverProps}
            onPointerDown={stopPropagation}
            onClick={stopPropagation}
        >
            {isEditing ? (
                <NodeEventInsulator>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wider opacity-70">Define type</span>
                        {onRequestClose ? (
                            <button
                                aria-label="Close type editor"
                                onClick={onRequestClose}
                                className="bg-transparent border-0 cursor-pointer p-0 opacity-70 hover:opacity-100"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        ) : null}
                    </div>
                    <ShapeTypeSelectorEditor value={editorInitialValue} restriction={editorRestriction} onChange={handleCustomTypeChange} />
                </NodeEventInsulator>
            ) : (
                <>
                    <TypeRenderer dataType={port.type} onPropertyClick={onPropertyClick} />
                    <PortQuickActionFooter port={port} side={side} />
                    {canEditType && onRequestEdit ? (
                        <button
                            type="button"
                            onPointerDown={stopPropagation}
                            onClick={event => {
                                event.stopPropagation();
                                onRequestEdit();
                            }}
                            className="w-full mt-2 pt-2 text-xs font-medium rounded-md px-2 py-1.5 transition-colors cursor-pointer flex items-center gap-1.5"
                            style={{
                                borderTop: `1px solid ${theme.separatorColor}`,
                                color: theme.primaryContrastColor,
                                backgroundColor: 'transparent',
                            }}
                            onMouseEnter={event => {
                                event.currentTarget.style.backgroundColor = theme.separatorColor;
                            }}
                            onMouseLeave={event => {
                                event.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <Settings size={11} />
                            Define type
                        </button>
                    ) : null}
                </>
            )}
        </div>
    );
};
